/** Founder loop (gap increment I2) — English strings (structure pinned to fr). */
import type { FounderKeys } from './fr';

const founderEn: FounderKeys = {
  common: {
    errorGeneric: 'Something went wrong. Please try again.',
    unavailable: 'Coming soon on this environment — nothing is made up in the meantime.',
    planRequired: 'This feature is not included in your plan.',
    readOnly: 'Read only',
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    open: 'Open',
    save: 'Save',
    saving: 'Saving…',
  },
  stages: {
    exploring: 'Exploring an idea',
    building: 'Building it',
    built_no_users: 'Built it, but no users yet',
    selling: 'Already selling',
    other: 'Other',
  },
  goalMetric: {
    users: 'First users',
    mrr: 'Monthly revenue (MRR)',
    other: 'Other',
  },
  nav: {
    today: 'Today',
    brief: 'Product brief',
    review: '5 · Weekly review',
  },
  onboarding: {
    dialogLabel: 'Getting-started assistant',
    skip: 'Skip',
    prev: 'Back',
    next: 'Next',
    finish: 'See today’s plan',
    finishing: 'Finishing…',
    stepOf: 'Step {{step}} of {{total}}',
    stage: {
      title: 'Where are you right now?',
      subtitle: 'Your stage and goal shape the whole plan.',
      stageLabel: 'Your stage',
      goalLabel: 'Your goal',
      metricLabel: 'Metric',
      targetLabel: 'Target',
      currencyLabel: 'Currency',
    },
    product: {
      title: 'Your product',
      subtitle: 'Paste your site URL, an App Store / Google Play listing, or describe your product in two sentences.',
    },
    review: {
      title: 'Your brief',
      subtitle: 'Check and correct it — everything stays editable later in “Product brief”.',
      none: 'Analyse your product in the previous step first.',
    },
    connect: {
      title: 'Connect a first channel',
      subtitle: 'Link a network to publish and measure. Optional — you can do it later.',
      loading: 'Loading platforms…',
      none: 'No platform is configured yet.',
      connected: 'Connected',
      connect: 'Connect',
      reconnect: 'Reconnect',
      opening: 'Opening…',
    },
    invite: {
      toggle: 'Working with others? Invite a teammate (optional)',
      email: 'Teammate email',
      role: 'Role',
      send: 'Send invitation',
      sending: 'Sending…',
      sent: 'Invitation sent to {{email}}.',
    },
    ready: {
      title: 'Your first plan is ready',
      subtitle: 'We create your strategy, your offering and your brand voice from the brief.',
      seeding: 'Generating the plan…',
      seeded: 'Strategy, offering and brand voice created.',
      noBrief: 'Without a brief you will start with a to-do to create one.',
    },
  },
  source: {
    typeLabel: 'Source',
    types: {
      url: 'Website',
      app_store: 'App Store',
      play_store: 'Google Play',
      text: 'Two sentences',
    },
    urlLabel: 'Your site URL',
    urlPlaceholder: 'yoursite.com',
    storeLabel: 'Listing link',
    storePlaceholder: 'https://apps.apple.com/…/id123456789',
    textLabel: 'Describe your product',
    textPlaceholder: 'What your product does, for whom, and the result they get.',
    attest: 'I confirm this site / listing is mine (or that I act for its owner).',
    attestHelp:
      'We read at most 5 public pages of this domain and honour its robots.txt. App Store listings use Apple’s official API.',
    attestRequired: 'Tick the ownership confirmation to run the analysis.',
    extract: 'Analyse',
    extracting: 'Analysing…',
    needsText: {
      client_rendered:
        'Your site renders with JavaScript, so its text cannot be read without a browser. Paste your homepage and pricing text below.',
      blocked_robots: 'Your robots.txt forbids reading this page. Paste its text below.',
      no_text: 'No usable text was found. Paste your page text below.',
    },
    pasteLabel: 'Your page text (homepage + pricing)',
    pastePlaceholder: 'Paste the headline, sub-headline, plans and prices exactly as they appear.',
    pasteSubmit: 'Analyse the pasted text',
  },
  brief: {
    title: 'Product brief',
    subtitle: 'What your site says about your product — extracted from your own pages, editable any time.',
    empty: 'No brief yet — analyse your site to get started.',
    analyzeTitle: 'Analyse my product',
    name: 'Product name',
    voice: 'Voice',
    version: 'Version {{version}}',
    versions: '{{count}} version(s)',
    llmOn: 'AI-enriched',
    llmOff: 'AI unavailable — deterministic extraction only',
    attestedOn: 'Ownership confirmed on {{date}}',
    hero: 'Hero (verbatim)',
    heroHeadline: 'Headline',
    heroSub: 'Sub-headline',
    cta: 'Call to action (verbatim)',
    ctaPrimary: 'Primary',
    ctaSecondary: 'Secondary',
    offer: 'Offer',
    pricing: 'Pricing',
    pricingPlan: 'Plan',
    pricingPrice: 'Price',
    pricingPeriod: 'Period',
    pricingPromo: 'Promo',
    pricingEmpty: 'No price detected on your pages.',
    currencyAssumed: 'assumed currency',
    promos: 'Promotions',
    geo: 'Geography',
    assumed: 'assumed',
    geoCountry: 'Country',
    geoCurrency: 'Currency',
    geoLocale: 'Language',
    audience: 'Audience — triangulated 3 ways',
    fromCopy: 'From the copy',
    fromPricing: 'From the pricing',
    fromTone: 'From the tone',
    consensus: 'Consensus',
    trust: 'Trust signals',
    trustPresent: 'Present',
    trustMissing: 'Missing',
    tone: 'Tone',
    keyMessages: 'Key messages',
    stage: 'Stage',
    goal: 'Goal',
    save: 'Save brief',
    saved: 'Brief saved (version {{version}}).',
    noChanges: 'Nothing to save.',
    reanalyze: 'Re-analyse',
    pages: 'Pages read',
    pageStatus: {
      ok: 'read',
      blocked_robots: 'blocked by robots.txt',
      client_rendered: 'JavaScript-rendered',
      error: 'unreachable',
      skipped_offsite: 'off-domain',
    },
    history: 'Version history',
    change: {
      extract: 'Analysis',
      edit: 'Edit',
    },
    seed: {
      title: 'Generate my plan',
      desc: 'Creates a marketing strategy grounded on this brief, a B2B offering (value prop + ICP) and a brand-voice profile.',
      button: 'Generate my plan',
      again: 'Regenerate my plan',
      running: 'Generating…',
      done: 'Plan generated.',
      strategy: 'Strategy',
      offering: 'Offering',
      voice: 'Brand voice',
      unavailable: 'Service unavailable',
      today: 'See today’s moves',
    },
  },
  trustKeys: {
    testimonials: 'Customer testimonials',
    customer_logos: 'Customer logos',
    reviews_ratings: 'Reviews and ratings',
    case_studies: 'Case studies',
    security_compliance: 'Security / compliance',
    guarantee: 'Guarantee / cancel anytime',
    press: 'Press mentions',
    team_founder: 'Team / founder',
    social_proof_numbers: 'Social-proof numbers',
  },
  competitors: {
    title: 'Competitors',
    desc: 'Up to 3 public competitor pages, each read once (robots.txt honoured).',
    urlPlaceholder: 'competitor.com',
    urlLabel: 'Competitor {{n}}',
    analyze: 'Compare',
    analyzing: 'Comparing…',
    gap: 'The angle you can own',
    pricingCmp: 'Pricing',
    onlyYou: 'Only you say',
    shared: 'In common',
    theyClaim: 'They claim',
    empty: 'No comparison yet.',
    needBrief: 'Create your brief first.',
    status: {
      ok: 'read',
      blocked_robots: 'blocked by robots.txt',
      client_rendered: 'JavaScript-rendered',
      error: 'unreachable',
    },
  },
  today: {
    title: 'Today',
    subtitle: 'Your moves for today, ranked — most important first.',
    now: 'Do this now',
    tabTodo: 'To do ({{count}})',
    tabDone: 'Completed ({{count}})',
    tabSkipped: 'Skipped ({{count}})',
    streak: 'Streak: {{count}} day(s)',
    minutes: '{{count}} min',
    why: 'Why',
    open: 'Open',
    guide: 'Step-by-step guide',
    complete: 'Complete',
    skip: 'Skip',
    reopen: 'Reopen',
    skipReasonLabel: 'Why are you skipping this move?',
    skipConfirm: 'Skip move',
    skippedReason: 'Skipped: {{reason}}',
    regenerate: 'Regenerate',
    regenerating: 'Regenerating…',
    regenerated: 'List updated.',
    personalized: 'personalised by AI',
    empty: 'Nothing to do right now — come back tomorrow!',
    doneEmpty: 'No completed move yet.',
    skippedEmpty: 'No skipped move.',
    completed: 'Move completed.',
    reopened: 'Move reopened.',
    skipped: 'Move skipped.',
    assistant: 'Getting-started assistant',
    sources: 'Where these moves come from',
    sourceStatus: {
      ok: 'active',
      empty: 'nothing to report',
      unavailable: 'unavailable',
      not_enabled: 'not enabled yet',
    },
    sourceNames: {
      product_brief: 'Product brief',
      strategy: 'Strategy',
      gtm_plan: 'This week’s plan',
      goals: 'Goals',
      weekly_checkin: 'Weekly review',
      autonomy: 'Autonomy',
      approvals: 'Approvals',
      advisor: 'AI advisor',
      conversations: 'Conversations to join',
      audit_fixes: 'Audit fixes',
      health_limits: 'Account health',
      launch_kit: 'Launch kit',
    },
    email: {
      title: 'Morning email',
      desc: 'Get this list every morning around 6 am (local time). Only you, turn it off any time.',
      toggle: 'Receive the morning email',
      recipient: 'Recipient: {{email}}',
      sendNow: 'Send me the preview now',
      sent: 'Email sent to {{email}}.',
      timezone: 'Time zone',
      on: 'Morning email turned on.',
      off: 'Morning email turned off.',
    },
  },
  widget: {
    title: 'Today’s moves',
    all: 'See all',
    empty: 'No open move today.',
  },
  weekly: {
    title: 'Weekly review',
    subtitle: 'Measure → ask you 2 to 4 questions → adjust next week’s plan.',
    loopOn: 'Weekly loop on: questions arrive every Monday.',
    loopOff:
      'Weekly loop off: questions are not generated automatically on Mondays. You can still run the review on demand.',
    sourceServer: 'platform setting',
    sourceTenant: 'your workspace setting',
    enableLoop: 'Turn on for my workspace',
    disableLoop: 'Turn off for my workspace',
    followServer: 'Follow the platform setting',
    runReview: 'Run this week’s review',
    running: 'Reviewing…',
    kpis: 'Indicators',
    total: 'Goals',
    behind: 'Behind',
    atRisk: 'At risk',
    onTrack: 'On track',
    proposals: 'Proposed levers',
    pacing: 'Pacing per goal',
    noPacing: 'No measurement yet — run the review.',
    progress: '{{progress}}% done · {{expected}}% expected',
    strategyBrief: 'Strategy brief',
  },
  checkin: {
    title: 'Your answers for week {{week}}',
    none: 'Run the review to get this week’s questions.',
    answered: 'Answers saved on {{date}} — plan re-planned for {{week}}.',
    submit: 'Send and re-plan',
    submitting: 'Re-planning…',
    resubmit: 'Change my answers',
    yes: 'Yes',
    no: 'No',
    count: 'How many?',
    number: 'Number',
    note: 'Detail (optional)',
    required: 'Answer every question.',
    q: {
      goal_gap:
        'The goal “{{goal}}” is at {{progress}}% (expected: {{expected}}%). Did you get results tracking missed (e.g. signups from elsewhere)? How many?',
      play_outcome:
        'The “{{title}}” play ({{channel}}) produced {{measured}} tracked result(s) this week. Did it bring you users, even outside tracking?',
      founder_time: 'How many hours can you spend on marketing next week?',
    },
  },
  diff: {
    title: 'Plan changes — week {{week}}',
    empty: 'No change yet — answer the check-in.',
    added: 'Added',
    removed: 'Removed',
    kept: 'Kept',
  },
  plays: {
    title: 'Plan drafts — week {{week}}',
    current: 'current',
    upcoming: 'upcoming',
    empty: 'No play planned — generate the GTM plan.',
    approve: 'Approve',
    approveEdited: 'Approve with edits',
    reject: 'Reject',
    editTitle: 'Edited title',
    confirmEdit: 'Approve the edited version',
    hours: '{{count}} h',
    rules: 'Rules to respect',
    evidence: 'Why this play',
    status: {
      planned: 'planned',
      approved: 'approved',
      rejected: 'rejected',
    },
  },
  autonomy: {
    title: 'Earned autonomy',
    desc: 'After {{count}} approvals in a row without edits in a lane, you are offered more autonomy (draft → suggest → auto). A rejection moves the lane down one rung.',
    streak: '{{streak}} / {{threshold}} without edits',
    promotionTitle: 'Promotion proposed: {{lane}} → {{rung}}',
    ack: 'I have read the Law 25 automated-decision notice and accept this mode for this lane.',
    accept: 'Accept promotion',
    decline: 'Decline',
    ackRequired: 'Tick the Law 25 notice to accept the promotion.',
    accepted: 'Lane {{lane}} moved to “{{rung}}”.',
    demoted: 'Rejection recorded — lane {{lane}} moves down to “{{rung}}”.',
    declined: 'Promotion declined.',
    proposed: 'An autonomy promotion is proposed for {{lane}}.',
  },
  rungs: {
    draft: 'Draft',
    suggest: 'Suggest',
    auto: 'Auto',
  },
  lanes: {
    content: 'Content',
    ads: 'Ads',
    community: 'Community',
    email: 'Email',
    prospecting: 'Prospecting',
    bio: 'Bio page',
  },
};

export default founderEn;
